import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchBins,
  fetchRacks,
  clearError,
  fetchInventoryStatus
} from '../store/slices/inventorySlice';
import { 
  Search, 
  RefreshCw,
  MapPin,
  BarChart3,
  AlertTriangle,
  Package
} from 'lucide-react';
import InventorySearch from '../components/inventory/InventorySearch';
import LocationOverview from '../components/inventory/LocationOverview';
import InventoryAdjustment from '../components/inventory/InventoryAdjustment';
import InventoryStatusCard from '../components/inventory/InventoryStatusCard';
import AvailableItems from '../components/inventory/AvailableItems';

const InventoryManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    bins,
    racks,
    locationsLoading,
    inventoryStatus,
    reportsLoading
  } = useAppSelector((state) => state.inventory);
  const { user } = useAppSelector((state) => state.auth);
  // Local state for UI
  const [activeTab, setActiveTab] = useState<'search' | 'available' | 'locations' | 'reports' | 'adjust'>('search');

  useEffect(() => {
    // Load initial data
    dispatch(fetchInventoryStatus());
    dispatch(fetchBins());
    dispatch(fetchRacks());
  }, [dispatch]);

  useEffect(() => {
    // Clear any errors after 5 seconds
    // Note: Error clearing is handled by individual error states
  }, [dispatch]);  const handleRefresh = () => {
    dispatch(clearError());
    dispatch(fetchInventoryStatus());
    dispatch(fetchBins());
    dispatch(fetchRacks());
  };const renderTabContent = () => {
    switch (activeTab) {
      case 'search':
        return <InventorySearch onLocationFound={(location) => console.log('Location found:', location)} />;
        case 'available':
        return <AvailableItems />;
      
      case 'locations':
        return (
          <LocationOverview 
            bins={bins}
            racks={racks}
            loading={locationsLoading}
          />
        );
      
      case 'reports':
        return (
          <div className="space-y-6">
            {inventoryStatus && (
              <InventoryStatusCard 
                status={inventoryStatus}
                loading={reportsLoading}
              />
            )}
            <div className="text-center py-8 text-gray-500">
              Additional reports will be available here
            </div>
          </div>
        );

      case 'adjust':
        if (!['manager'].includes(user?.role || '')) {
          return (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-gray-600">You don't have permission to adjust inventory.</p>
              <p className="text-sm text-gray-500 mt-2">Only managers can make inventory adjustments.</p>
            </div>
          );
        }
        return <InventoryAdjustment />;
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>          <h1 className="text-2xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-gray-600">Search inventory, manage locations, and monitor stock levels</p>
        </div>        <button
          onClick={handleRefresh}
          disabled={locationsLoading}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${locationsLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>      </div>      {/* Tab Navigation with improved descriptions */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200 px-6 py-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('search')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex flex-col items-center space-y-1 ${
                activeTab === 'search'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Search className="h-4 w-4 mr-2" />
                Inventory Search
              </div>
              <span className="text-xs text-gray-400 font-normal">Find items by location</span>
            </button>

            <button
              onClick={() => setActiveTab('available')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex flex-col items-center space-y-1 ${
                activeTab === 'available'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <Package className="h-4 w-4 mr-2" />
                Available Items
              </div>
              <span className="text-xs text-gray-400 font-normal">Browse available stock</span>
            </button>

            <button
              onClick={() => setActiveTab('locations')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex flex-col items-center space-y-1 ${
                activeTab === 'locations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-2" />
                Locations
              </div>
              <span className="text-xs text-gray-400 font-normal">Manage bins & racks</span>
            </button>

            <button
              onClick={() => setActiveTab('reports')}
              className={`py-3 px-1 border-b-2 font-medium text-sm flex flex-col items-center space-y-1 ${
                activeTab === 'reports'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <BarChart3 className="h-4 w-4 mr-2" />
                Reports
              </div>
              <span className="text-xs text-gray-400 font-normal">Inventory analytics</span>
            </button>

            {user?.role === 'manager' && (
              <button
                onClick={() => setActiveTab('adjust')}
                className={`py-3 px-1 border-b-2 font-medium text-sm flex flex-col items-center space-y-1 ${
                  activeTab === 'adjust'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <Package className="h-4 w-4 mr-2" />
                  Adjustments
                </div>
                <span className="text-xs text-gray-400 font-normal">Manager only</span>
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div>
          {renderTabContent()}
        </div>
      </div>    </div>
  );
};

export default InventoryManagement;
